import React, { useState, useEffect } from 'react';
import { Plus, FileText, Printer, Trash2, Edit, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SavedAGB, AGB_STORAGE_KEY, getIncompleteAGBFields, migrateAGB } from '@/lib/constants/agbTemplateData';
import AGBForm from './AGBForm';
import AGBPreview from './AGBPreview';
import AGBDocumentRenderer from './AGBDocumentRenderer';

type View = 'list' | 'form' | 'preview' | 'print';

const billingModelLabels: Record<string, string> = {
  monthly: 'Monatlich',
  yearly: 'Jährlich',
  one_time_per_user: 'Einmal/Nutzer',
  credits_plus_fixed: 'Credits+Fix',
  cooperation: 'Kooperation',
};

const AGBDashboard = () => {
  const [savedAGBs, setSavedAGBs] = useState<SavedAGB[]>([]);
  const [currentView, setCurrentView] = useState<View>('list');
  const [selectedAGB, setSelectedAGB] = useState<SavedAGB | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(AGB_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSavedAGBs(parsed.map(migrateAGB));
      } catch { /* ignore */ }
    }
  }, []);

  const persistAGBs = (agbs: SavedAGB[]) => {
    setSavedAGBs(agbs);
    localStorage.setItem(AGB_STORAGE_KEY, JSON.stringify(agbs));
  };

  const handleSave = (agb: SavedAGB) => {
    const exists = savedAGBs.findIndex(a => a.id === agb.id);
    const updated = exists >= 0
      ? savedAGBs.map(a => a.id === agb.id ? { ...agb, updatedAt: new Date().toISOString() } : a)
      : [...savedAGBs, agb];
    persistAGBs(updated);
    setSelectedAGB(agb);
    setCurrentView('preview');
  };

  const handleDelete = (id: string) => {
    persistAGBs(savedAGBs.filter(a => a.id !== id));
  };

  const handleFinalize = (id: string) => {
    persistAGBs(savedAGBs.map(a => a.id === id ? { ...a, status: 'finalized' as const, updatedAt: new Date().toISOString() } : a));
    setSelectedAGB(prev => prev && prev.id === id ? { ...prev, status: 'finalized' } : prev);
  };

  if (currentView === 'form') {
    return (
      <AGBForm
        existingAGB={selectedAGB}
        onSave={handleSave}
        onCancel={() => { setCurrentView('list'); setSelectedAGB(null); }}
      />
    );
  }

  if (currentView === 'preview' && selectedAGB) {
    return (
      <AGBPreview
        agb={selectedAGB}
        onBack={() => setCurrentView('list')}
        onEdit={() => setCurrentView('form')}
        onPrint={() => setCurrentView('print')}
        onFinalize={() => handleFinalize(selectedAGB.id)}
      />
    );
  }

  if (currentView === 'print' && selectedAGB) {
    return (
      <AGBDocumentRenderer
        agb={selectedAGB}
        onBack={() => setCurrentView('preview')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Allgemeine Geschäftsbedingungen (B2B)</h2>
          <p className="text-muted-foreground mt-1">
            AGB-Generator für institutionelle SaaS-Lizenzverträge — 11 Paragraphen
          </p>
        </div>
        <Button onClick={() => { setSelectedAGB(null); setCurrentView('form'); }}>
          <Plus className="h-4 w-4 mr-2" />
          Neue AGB erstellen
        </Button>
      </div>

      {savedAGBs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Noch keine AGB erstellt</h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              Erstellen Sie Allgemeine Geschäftsbedingungen für Ihre institutionellen Kooperationspartner.
              11 Paragraphen werden automatisch generiert, variable Felder individuell angepasst.
            </p>
            <Button onClick={() => { setSelectedAGB(null); setCurrentView('form'); }}>
              <Plus className="h-4 w-4 mr-2" />
              Erste AGB erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {savedAGBs.map(agb => (
            <Card key={agb.id} className="hover:border-primary/50 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{agb.customer.name || 'Unbenannter Kunde'}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {agb.commercialTerms.pricing || 'Vergütung offen'} · {agb.serviceScope.seats} Seats
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {billingModelLabels[agb.commercialTerms.billingModel] || 'Jährlich'}
                    </Badge>
                    {agb.contractTerms.tenantIsolation && (
                      <Badge variant="outline" className="text-xs">Mandant</Badge>
                    )}
                    {agb.status === 'draft' && getIncompleteAGBFields(agb).length > 0 && (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-500 dark:text-yellow-400">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Unvollständig
                      </Badge>
                    )}
                    <Badge variant={agb.status === 'finalized' ? 'default' : 'secondary'}>
                      {agb.status === 'finalized' ? 'Finalisiert' : 'Entwurf'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Erstellt: {new Date(agb.createdAt).toLocaleDateString('de-DE')}
                    {agb.updatedAt !== agb.createdAt && (
                      <> · Aktualisiert: {new Date(agb.updatedAt).toLocaleDateString('de-DE')}</>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setSelectedAGB(agb); setCurrentView('preview'); }}>
                      <FileText className="h-4 w-4 mr-1" />
                      Ansehen
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedAGB(agb); setCurrentView('form'); }}>
                      <Edit className="h-4 w-4 mr-1" />
                      Bearbeiten
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedAGB(agb); setCurrentView('print'); }}>
                      <Printer className="h-4 w-4 mr-1" />
                      Drucken
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(agb.id)} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AGBDashboard;
